import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { confirm } from '@/stores/confirmStore';
import { useCompanyStore } from '../../../../stores/companyStore';
import { useInvoiceEdit, InvoiceEditData } from '../../../../hooks/useInvoiceEdit';
import { useTaxRates } from '../../../../hooks/useTaxRates';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { isVatExempt } from '../../../../utils/vatCalculations';
import { EditableItemsTable } from '../../shared/editable/EditableItemsTable';
import { EditableClientSection } from '../../shared/editable/EditableClientSection';
import { EditableNotesSection } from '../../shared/editable/EditableNotesSection';
import { EditableDiscountSection } from '../../shared/editable/EditableDiscountSection';
import { EditActionButtons } from '../../shared/editable/EditActionButtons';
import { EditableInvoiceExtendedFields } from '../../shared/editable/EditableInvoiceExtendedFields';
import { CompanyInfoSection } from '../../shared/CompanyInfoSection';
import { TotalsSection } from '../../shared/TotalsSection';
import { LegalFooter } from '../../shared/LegalFooter';
import { ProductSelector } from '@/features/catalog/components/ProductSelector';
import { Dashboard } from '../../../Dashboard';
import type { TemplateSettings, Company } from '../../types';

interface InvoiceTemplateCorporateEditProps {
  initialData: InvoiceEditData;
  template: TemplateSettings;
  company: Company | null;
  color: string;
}

/**
 * Template Agency (STARTER)  - Mode édition FACTURE
 * - Logo: top-right
 * - Style: ultra-minimaliste noir/blanc
 * - Table: minimal
 */
export const InvoiceTemplateCorporateEdit: React.FC<InvoiceTemplateCorporateEditProps> = ({
  initialData,
  template,
  company,
  color: colorProp,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company: companyStore } = useCompanyStore();
  const finalCompany = company || companyStore;

  const vatRegime = finalCompany?.vatRegime;
  const isExempt = isVatExempt(vatRegime);

  // ✅ Charger les TaxRates une seule fois au niveau du composant parent
  const { taxRates, loading: loadingTaxRates, defaultTaxRate } = useTaxRates();
  const { features: companyFeatures } = useCompanyFeatures();

  const {
    invoiceData,
    isSaving,
    hasChanges,
    updateField,
    updateDiscountType,
    updateDiscountValue,
    updateItem,
    addItem,
    removeItem,
    addSectionTitle,
    addComment,
    addSubtotal,
    addProductFromCatalog,
    saveInvoice,
  } = useInvoiceEdit(
    initialData,
    vatRegime,
    undefined,  // onSuccess callback
    // ✅ Passer le taux par défaut au hook pour initialiser les nouveaux items
    defaultTaxRate ? { id: defaultTaxRate.id, rate: Number(defaultTaxRate.rate) } : undefined
  );

  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);

  const handleCancel = async () => {
    if (hasChanges) {
      const confirmed = await confirm({
        title: t('templates.common.unsavedChangesTitle'),
        message: t('templates.common.unsavedChangesMessage'),
        variant: 'warning',
        confirmText: t('templates.common.quit'),
        cancelText: t('templates.common.cancel'),
      });
      if (!confirmed) return;
    }
    navigate('/invoices');
  };

  const handleVisualize = () => {
    if (invoiceData.id) {
      navigate(`/invoices/${invoiceData.id}`);
    }
  };

  const handleSelectClient = (client: any) => {
    if (!client) return;

    // ✅ CRITIQUE: Capturer le clientId (obligatoire pour backend)
    updateField('clientId', client.id);
    updateField('clientName', client.fullName || client.companyName || '');
    updateField('clientCompany', client.companyName || '');
    updateField('clientEmail', client.email || '');
    updateField('clientPhone', client.phone || '');
    updateField('clientAddressLine1', client.addressLine1 || client.address || '');
    updateField('clientCity', client.city || '');
    updateField('clientZipCode', client.zipCode || client.postalCode || '');
    updateField('clientCountry', client.countryCode || client.country || 'FR');
  };

  // Convertir les données invoice pour le Dashboard (format QuoteData)
  const convertToQuoteDataForDashboard = (): any => ({
    quoteNumber: invoiceData.invoiceNumber,
    date: invoiceData.date,
    validUntil: invoiceData.dueDate,
    company: {
      name: finalCompany?.legalName || finalCompany?.tradeName || '',
      address: finalCompany?.addressLine1 || '',
      city: finalCompany?.city || '',
      zipCode: finalCompany?.zipCode || '',
      country: finalCompany?.taxCountryCode || 'FR',
      phone: finalCompany?.phone || '',
      email: finalCompany?.email || '',
    },
    client: {
      name: invoiceData.clientName,
      company: invoiceData.clientCompany,
      address: invoiceData.clientAddressLine1,
      city: invoiceData.clientCity,
      zipCode: invoiceData.clientZipCode,
      country: 'FR',
      phone: invoiceData.clientPhone,
      email: invoiceData.clientEmail,
    },
    items: invoiceData.items,
    eventTitle: invoiceData.title,  // ✅ Aligné sur Prisma
    notes: invoiceData.internalNotes,
    conditions: '',
    discountType: invoiceData.discountType,
    discountValue: invoiceData.discountValue,
    discountAmount: invoiceData.discountAmount,
  });

  // Ventilation TVA non transmise au composant (on s'appuie sur subtotal/tax/total)

  return (
    <div className="min-h-screen bg-transparent">
      {/* Contenu principal - Style minimaliste */}
      <div className="max-w-4xl mx-auto py-12 px-4">
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-8">
          {/* Header ultra-minimaliste */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-12 pb-8 border-b border-white/30 gap-6">
            {/* Facture info LEFT */}
            <div className="flex-1">
              <h1 className="text-6xl md:text-7xl font-thin tracking-tighter text-gray-900 mb-4">
                {t('templates.invoice.title')}
              </h1>
              <div className="text-sm font-mono text-gray-600">
                {invoiceData.invoiceNumber}
              </div>

              {/* Dates éditables */}
              <div className="mt-6 space-y-3 text-sm font-mono">
                <div className="flex gap-4 items-center">
                  <span className="text-gray-500 uppercase tracking-wider text-xs w-32">{t('templates.quote.date')}</span>
                  <input
                    type="date"
                    value={invoiceData.date}
                    onChange={(e) => updateField('date', e.target.value)}
                    className="px-3 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                  />
                </div>
                <div className="flex gap-4 items-center">
                  <span className="text-gray-500 uppercase tracking-wider text-xs w-32">{t('templates.common.dueDate')}</span>
                  <input
                    type="date"
                    value={invoiceData.dueDate}
                    onChange={(e) => updateField('dueDate', e.target.value)}
                    className="px-3 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                  />
                </div>
              </div>
            </div>

            {/* Company Info (Logo RIGHT) */}
            <div className="md:text-right flex-shrink-0">
                <CompanyInfoSection
                  company={finalCompany}
                  companyName={invoiceData.companyName}
                  companyAddress={invoiceData.companyAddressLine1}
                  companyCity={invoiceData.companyCity}
                  companyPostalCode={invoiceData.companyZipCode}
                  companyPhone={invoiceData.companyPhone}
                  companyEmail={invoiceData.companyEmail}
                  logoPosition="top-right"
                />
            </div>
          </div>

          {/* Section Client Éditable */}
          <div className="mb-12 max-w-md">
            <EditableClientSection
              data={invoiceData}
              onUpdate={updateField}
              onSelectClient={handleSelectClient}
              accentColor="#000000"
            />
          </div>

          {/* Titre du document (éditable) - Style Corporate minimaliste */}
          <div className="mb-8">
            <input
              type="text"
              value={invoiceData.title || ''}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder={t('templates.invoice.subjectPlaceholder')}
              className="w-full px-0 py-2 text-xl font-thin tracking-wide bg-transparent border-0 border-b border-gray-300 focus:ring-0 focus:border-gray-900 transition-all duration-300 placeholder:text-gray-300"
              style={{ color: '#000000' }}
            />
          </div>

          {/* Tableau des items éditable - Style minimal */}
          <EditableItemsTable
            items={invoiceData.items}
            onUpdateItem={updateItem}
            onAddItem={addItem}
            onRemoveItem={removeItem}
            onAddFromCatalog={() => setShowProductSelector(true)}
            tableStyle="minimal"
            accentColor="#000000"
            showVat={!isExempt}
            taxRates={taxRates}
            loadingTaxRates={loadingTaxRates}
            onAddSectionTitle={addSectionTitle}
            onAddComment={addComment}
            onAddSubtotal={addSubtotal}
            features={{
              showReference: companyFeatures?.referenceSKU ?? true,
              showUnit: companyFeatures?.unitOfMeasure ?? true,
              showDiscount: companyFeatures?.lineDiscount ?? true,
              showSections: companyFeatures?.sections ?? true,
            }}
          />

          {/* Section Remise éditable */}
          <div className="mt-8">
            <EditableDiscountSection
              data={invoiceData}
              onUpdateType={updateDiscountType}
              onUpdateValue={updateDiscountValue}
              accentColor="#000000"
            />
          </div>

          {/* Section Totaux */}
          <TotalsSection
            subtotal={invoiceData.subtotal}
            tax={invoiceData.tax}
            total={invoiceData.total}
            discount={invoiceData.discountAmount}
            discountType={invoiceData.discountType}
            discountValue={invoiceData.discountValue}
            accentColor="#000000"
            showVatBreakdown={!isExempt}
          />

          {/* Section Notes éditable */}
          <div className="mt-12">
            <EditableNotesSection
              data={invoiceData}
              onUpdate={updateField}
              accentColor="#000000"
            />
          </div>

          {/* Champs étendus (titre, conditions générales) */}
          <div className="mt-8">
            <EditableInvoiceExtendedFields
              data={invoiceData}
              onUpdate={updateField}
              accentColor="#000000"
            />
          </div>

          {/* Footer légal - Style minimaliste */}
          <LegalFooter company={finalCompany} notes={invoiceData.termsConditions} />
        </div>
      </div>

      {/* Dashboard Modal */}
      {showDashboard && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55] flex items-center justify-center p-2 md:p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 w-full md:max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-white/30 px-4 md:px-6 py-3 md:py-4 flex justify-between items-center">
              <h2 className="text-lg md:text-xl font-bold text-gray-800">
                {t('templates.common.dashboardTitle')}
              </h2>
              <button
                onClick={() => setShowDashboard(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl flex-shrink-0 ml-2 p-2 hover:bg-white/50 rounded-xl transition-all duration-300"
              >
                ×
              </button>
            </div>
            <div className="p-4 md:p-6">
              <Dashboard
                quoteData={convertToQuoteDataForDashboard()}
                onUpdateQuote={() => {}}
                onSave={saveInvoice}
              />
            </div>
          </div>
        </div>
      )}

      {/* Product Selector Modal */}
      {showProductSelector && (
        <ProductSelector
          onSelectProduct={async (product: any) => {
            await addProductFromCatalog(product);
            setShowProductSelector(false);
          }}
          onClose={() => setShowProductSelector(false)}
        />
      )}

      {/* Boutons d'actions flottants */}
      <EditActionButtons
        onSave={saveInvoice}
        onCancel={handleCancel}
        onShowDashboard={() => setShowDashboard(true)}
        onVisualize={handleVisualize}
        isSaving={isSaving}
        hasChanges={hasChanges}
        accentColor="#000000"
        documentType="invoice"
      />
    </div>
  );
};
